import { ReactNode, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { toast } from 'react-toastify';
import axios from '../../../utils/axios';
import GstinVerifyDialog, { GstinSummary, appBtnSx } from './GstinVerifyDialog';

const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const SILENT = { headers: { 'x-silent': '1' } };
const errMsg = (e: any, fallback: string) => e?.response?.data?.message || fallback;

interface Options {
  // Current field value, so the badge reverts when the GSTIN is edited.
  value: string;
  // Called with the verified portal summary so the parent fills its own fields.
  onVerified: (summary: GstinSummary) => void;
  // Normalises the value before verifying (e.g. uppercase the field).
  onNormalize?: (gstin: string) => void;
  disabled?: boolean;
}

interface GstinVerify {
  // endAdornment for the GST Number field: Verify button, or a Verified badge.
  adornment: ReactNode;
  // Captcha image for the grid column beside the GST Number field; null while not verifying.
  captcha: ReactNode;
  // Code box and buttons for the row below; null while not verifying.
  panel: ReactNode;
  // Popup with the portal details and "Fill form"; render anywhere in the form.
  dialog: ReactNode;
}

/**
 * GSTIN verification against the GST portal. Verify -> captcha appears inline under
 * the field -> on success the details popup opens with "Fill form".
 */
export const useGstinVerify = ({ value, onVerified, onNormalize, disabled }: Options): GstinVerify => {
  const [gstin, setGstin] = useState('');
  const [verified, setVerified] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<GstinSummary | null>(null);
  // Kept after verification so "Fill form" can be used again later.
  const [verifiedSummary, setVerifiedSummary] = useState<GstinSummary | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [playing, setPlaying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Speaker: the portal reads out the captcha on screen.
  const playAudio = async () => {
    if (!sessionId || playing) return;
    setPlaying(true);
    try {
      const res = await axios.post('/api/v1/gst-lookup/captcha/audio', { sessionId }, SILENT);
      audioRef.current?.pause();
      const audio = new Audio(res.data.data.captchaAudio);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => setPlaying(false);
      await audio.play();
    } catch (e: any) {
      setPlaying(false);
      setError(errMsg(e, 'Could not play the audio captcha'));
    }
  };

  const current = String(value || '').trim().toUpperCase();
  const isVerified = !!verified && current === verified;
  const open = !!gstin;

  const startSession = async (target: string) => {
    setLoading(true);
    setError('');
    setCaptcha('');
    try {
      const res = await axios.post('/api/v1/gst-lookup/captcha', { mode: 'gstin', query: target }, SILENT);
      setSessionId(res.data.data.sessionId);
      setCaptchaImage(res.data.data.captchaImage);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e: any) {
      setError(errMsg(e, 'Could not reach the GST portal, try again'));
    } finally {
      setLoading(false);
    }
  };

  const begin = () => {
    if (!GSTIN_RX.test(current)) {
      toast.error('Enter a valid 15 character GSTIN first');
      return;
    }
    onNormalize?.(current);
    setGstin(current);
    setSummary(null);
    startSession(current);
  };

  const cancelSession = () => {
    audioRef.current?.pause();
    setPlaying(false);
    if (sessionId) axios.delete(`/api/v1/gst-lookup/session/${sessionId}`, SILENT).catch(() => undefined);
    setSessionId('');
    setCaptchaImage('');
    setCaptcha('');
    setError('');
  };

  const close = () => {
    cancelSession();
    setGstin('');
  };

  const refreshCaptcha = async () => {
    if (!sessionId) return startSession(gstin);
    setLoading(true);
    try {
      const res = await axios.post('/api/v1/gst-lookup/captcha/refresh', { sessionId }, SILENT);
      setCaptchaImage(res.data.data.captchaImage);
      setCaptcha('');
      inputRef.current?.focus();
    } catch {
      await startSession(gstin);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (captcha.trim().length < 6) {
      setError('Enter the 6 character captcha');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post('/api/v1/gst-lookup/search', { sessionId, captcha: captcha.trim() }, SILENT);
      const s: GstinSummary = res.data.data.summary;
      setSessionId('');
      setCaptchaImage('');
      setCaptcha('');
      setSummary(s);
      setVerifiedSummary(s);
      setVerified(s.gstin);
    } catch (e: any) {
      const body = e?.response?.data;
      if (body?.retry && body?.data?.captchaImage) {
        setCaptchaImage(body.data.captchaImage);
        setCaptcha('');
        setError(body.message || 'Captcha did not match, try again');
        inputRef.current?.focus();
      } else {
        setSessionId('');
        setCaptchaImage('');
        setError(errMsg(e, 'GST portal did not respond, try again'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const use = (s: GstinSummary) => {
    onVerified(s);
    toast.success('Details filled from GST portal');
    setGstin('');
    setSummary(null);
    setShowDetails(false);
  };

  const closeDetails = () => {
    setShowDetails(false);
    close();
  };

  const adornment = (
    <InputAdornment position='end'>
      {isVerified ? (
        <>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12 }}>
            <VerifiedIcon sx={{ fontSize: 14 }} /> Verified
          </span>
        </>
      ) : (
        <Button size='small' variant='text' onClick={begin} disabled={disabled || open} startIcon={<VerifiedOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{ minWidth: 0, px: 0.5, fontSize: 12, textTransform: 'none', color: '#488dff' }}>
          Verify
        </Button>
      )}
    </InputAdornment>
  );

  // Everything stacked in the column beside the GST Number field: image, code box, buttons.
  const captcha_ = open && !summary ? (
    <>
      <h5 className='c-label'>Captcha from GST portal</h5>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {loading ? (
          <Box sx={{ width: 180, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
        ) : captchaImage ? (
          // The image fills its own box edge to edge, no padding around it
          <img src={captchaImage} alt='captcha' style={{ height: 44, width: 'auto', display: 'block', border: '1px solid #e9ecf1', borderRadius: 8 }} />
        ) : (
          <Typography variant='body2' color='error'>Captcha unavailable</Typography>
        )}
        <IconButton size='small' onClick={playAudio} disabled={loading || submitting || !captchaImage || playing} title='Listen to the captcha' sx={{ color: '#488dff' }}><VolumeUpIcon fontSize='small' /></IconButton>
        <IconButton size='small' onClick={refreshCaptcha} disabled={loading || submitting} title='New captcha' sx={{ color: '#488dff' }}><RefreshIcon fontSize='small' /></IconButton>
      </Box>
      <TextField
        inputRef={inputRef}
        className='c-text-field'
        variant='standard'
        size='small'
        placeholder='Enter captcha'
        value={captcha}
        inputProps={{ maxLength: 6 }}
        onChange={(e) => setCaptcha(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        disabled={loading || submitting || !captchaImage}
        sx={{ width: '100%', mt: 1 }}
        InputProps={{ disableUnderline: true }}
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button variant='contained' size='small' sx={appBtnSx} onClick={submit} disabled={loading || submitting || !captchaImage}>
          {submitting ? <CircularProgress size={16} sx={{ color: '#488dff' }} /> : 'Verify GSTIN'}
        </Button>
        <Button variant='contained' size='small' sx={appBtnSx} onClick={close} disabled={submitting}>Cancel</Button>
      </Box>
      {error && <Typography variant='body2' color='error' sx={{ mt: 0.5 }}>{error}</Typography>}
    </>
  ) : isVerified && verifiedSummary ? (
    // Verified: the same column offers the portal details again, as a proper button.
    <>
      <h5 className='c-label'>&nbsp;</h5>
      <Button variant='outlined' size='small' sx={{ display: 'flex', mt: 0.5, height: 35, px: 1.5, fontSize: 13, textTransform: 'none' }} onClick={() => setShowDetails(true)} disabled={disabled}>
        Use GST Details
      </Button>
    </>
  ) : null;

  // Nothing on the next row any more; kept so the forms need no change if it returns.
  const panel = null;

  const dialog = showDetails
    ? <GstinVerifyDialog open summary={verifiedSummary} onClose={closeDetails} onUse={use} />
    : <GstinVerifyDialog open={open && !!summary} summary={summary} onClose={close} onUse={use} />;

  return { adornment, captcha: captcha_, panel, dialog };
};

export default useGstinVerify;
